export function chooseProducts(recommended, fallback, cartIds, limit = 3) {
  const seen = new Set(cartIds.map(String));
  return [...recommended, ...fallback].filter(product => {
    const id = String(product.id);
    if (seen.has(id) || product.available === false || /shipping|insurance|gift-card/.test(product.handle || '')) return false;
    seen.add(id);
    return true;
  }).slice(0, limit);
}

export function variantView(product, id) {
  const variant = product.variants.find(v => String(v.id) === String(id) && v.available);
  return variant ? {id: variant.id, price: variant.price, compare: variant.compare_at_price || 0, image: variant.featured_image?.src || product.featured_image || ''} : null;
}

if (typeof window !== 'undefined' && !customElements.get('ds-cart-upsell')) {
  let adding = false;
  const cache = new Map();
  class DinoCartUpsell extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.config = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      this.root = this.config.root;
      this.track = this.querySelector('[data-upsell-track]');
      this.status = this.querySelector('[data-upsell-status]');
      this.addEventListener('click', event => {
        const nav = event.target.closest('[data-upsell-step]');
        if (nav) this.goToCard((this.slideAnimations ? this.activeIndex : this.cardIndex()) + Number(nav.dataset.upsellStep), true);
      }, {signal: this.controller.signal});
      this.track.addEventListener('scroll', () => { if (!this.slideAnimations) this.updateArrows(); }, {signal:this.controller.signal,passive:true});
      for (const type of ['touchstart', 'pointerdown', 'wheel']) {
        this.track.addEventListener(type, () => this.stopSlide(), {signal:this.controller.signal,passive:true});
      }
      this.activeIndex = 0;
      this.trackWidth = this.track.clientWidth;
      this.observer = new ResizeObserver(() => {
        const width = this.track.clientWidth;
        if (!width || width === this.trackWidth) return;
        this.trackWidth = width;
        this.goToCard(this.activeIndex);
      });
      this.observer.observe(this.track);
      this.load();
    }
    disconnectedCallback() {
      this.stopSlide();
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = null;
    }
    async get(path) {
      const response = await fetch(this.root + path, {signal:this.controller.signal,headers:{Accept:'application/json'}});
      if (!response.ok) throw new Error('load');
      return response.json();
    }
    async product(handle) {
      if (cache.has(handle)) return cache.get(handle);
      const product = await this.get(`products/${encodeURIComponent(handle)}.js`);
      cache.set(handle, product);
      return product;
    }
    async collectionProducts(handle) {
      const params = new URLSearchParams({section_id:'ds-upsell-products'});
      if (this.config.sort !== 'manual') params.set('sort_by', this.config.sort || 'best-selling');
      const response = await fetch(`${this.root}collections/${encodeURIComponent(handle)}?${params}`, {signal:this.controller.signal});
      if (!response.ok) return [];
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      return JSON.parse(doc.querySelector('[data-ds-collection-products]')?.textContent || '[]');
    }
    async load() {
      try {
        const limit = Math.max(1, Math.min(12, Number(this.config.limit) || 3));
        const products = [];
        const tried = new Set();
        const fill = async candidates => {
          for (const candidate of chooseProducts(candidates, [], this.config.cartIds, candidates.length)) {
            if (products.length >= limit) break;
            if (tried.has(candidate.id)) continue;
            tried.add(candidate.id);
            try {
              const product = await this.product(candidate.handle);
              if (product.available && product.variants.some(v=>v.available)) products.push(product);
            } catch (error) { if (error.name === 'AbortError') throw error; }
          }
        };
        for (const handle of this.config.collections || []) {
          if (products.length >= limit) break;
          try { await fill(await this.collectionProducts(handle)); }
          catch (error) { if (error.name === 'AbortError') throw error; }
        }
        if (products.length < limit) {
          try { await fill((await this.get(`recommendations/products.json?product_id=${this.config.seed}&limit=10&intent=related`)).products || []); }
          catch (error) { if (error.name === 'AbortError') throw error; }
        }
        if (products.length < limit) {
          try { await fill(await this.collectionProducts('best-sellers')); }
          catch (error) { if (error.name === 'AbortError') throw error; }
        }
        if (!this.isConnected || this.controller.signal.aborted) return;
        this.track.replaceChildren(...products.map(product => this.card(product)));
        this.hidden = products.length === 0;
        requestAnimationFrame(() => this.updateArrows());
      } catch (error) {
        if (error.name !== 'AbortError') this.hidden = true;
      }
    }
    money(cents) {
      return new Intl.NumberFormat(this.config.locale, {style:'currency',currency:this.config.currency}).format(cents / 100);
    }
    cardPositions() {
      const origin = this.track.getBoundingClientRect().left;
      return Array.from(this.track.children, card =>
        card.getBoundingClientRect().left - origin + this.track.scrollLeft);
    }
    cardIndex() {
      const positions = this.cardPositions();
      return positions.reduce((nearest, left, index) =>
        Math.abs(left - this.track.scrollLeft) < Math.abs(positions[nearest] - this.track.scrollLeft) ? index : nearest, 0);
    }
    stopSlide(restoreSnap = true) {
      if (this.slideAnimations) {
        const transform = getComputedStyle(this.track.firstElementChild).transform;
        const offset = transform === 'none' ? 0 : new DOMMatrixReadOnly(transform).m41;
        const position = this.track.scrollLeft - offset;
        this.slideAnimations.forEach(animation => animation.cancel());
        this.slideAnimations = null;
        this.track.scrollTo({left: position, behavior: 'instant'});
      }
      if (restoreSnap && this.savedSnap !== undefined) {
        this.track.style.scrollSnapType = this.savedSnap;
        this.savedSnap = undefined;
        this.track.removeAttribute('data-sliding');
        this.updateArrows();
      }
    }
    goToCard(index, animate = false) {
      this.stopSlide(false);
      const positions = this.cardPositions();
      if (!positions.length || !this.track.clientWidth) return;
      const start = this.track.scrollLeft;
      this.activeIndex = Math.max(0, Math.min(positions.length - 1, index));
      const target = positions[this.activeIndex];
      if (this.savedSnap === undefined) this.savedSnap = this.track.style.scrollSnapType;
      this.track.style.scrollSnapType = 'none';
      const finish = () => {
        this.slideAnimations?.forEach(animation => animation.cancel());
        this.slideAnimations = null;
        this.track.scrollTo({left: target, behavior: 'instant'});
        this.stopSlide();
      };
      if (!animate || matchMedia('(prefers-reduced-motion: reduce)').matches || Math.abs(target - start) < 1) {
        finish();
        return;
      }
      this.track.setAttribute('data-sliding', '');
      this.updateArrows(false);
      const animations = Array.from(this.track.children, card => card.animate([
        {transform: 'translate3d(0, 0, 0)'},
        {transform: `translate3d(${start - target}px, 0, 0)`}
      ], {duration: 320, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'forwards'}));
      this.slideAnimations = animations;
      Promise.all(animations.map(animation => animation.finished)).then(() => {
        if (this.slideAnimations === animations) finish();
      }, () => {});
    }
    updateArrows(readPosition = true) {
      if (readPosition) this.activeIndex = this.cardIndex();
      this.querySelector('[data-upsell-step="-1"]').disabled = this.activeIndex === 0;
      this.querySelector('[data-upsell-step="1"]').disabled = this.activeIndex >= this.track.children.length - 1;
    }
    card(product) {
      const card = this.querySelector('template').content.firstElementChild.cloneNode(true);
      const select = card.querySelector('select');
      const image = card.querySelector('img');
      const button = card.querySelector('button');
      const title = card.querySelector('[data-upsell-title]');
      title.textContent = product.title;
      select.setAttribute('aria-label', `${product.options.map(o=>typeof o==='string'?o:o.name).join(' / ')}: ${product.title}`);
      product.variants.filter(v=>v.available).forEach(variant => select.add(new Option(variant.title, variant.id)));
      image.alt = product.title;
      const update = () => {
        const view = variantView(product, select.value);
        button.disabled = !view || adding;
        if (!view) return;
        if (view.image) {
          const url = new URL(view.image, location.origin);
          url.searchParams.set('width','360');
          image.src = url.href;
        }
        card.querySelectorAll('a').forEach(a=>a.href=`${this.root}products/${product.handle}?variant=${view.id}`);
        card.querySelector('[data-upsell-price]').textContent = this.money(view.price);
        const compare = card.querySelector('s');
        compare.hidden = view.compare <= view.price;
        compare.textContent = this.money(view.compare);
      };
      select.addEventListener('change', update);
      card.querySelector('form').addEventListener('submit', event => {
        event.preventDefault();
        const view = variantView(product, select.value);
        if (view && !adding) this.add(view, button);
      });
      update();
      return card;
    }
    async add(view, button) {
      if (adding) return;
      adding = true;
      this.status.textContent = '';
      this.querySelectorAll('[data-upsell-add]').forEach(b=>b.disabled=true);
      button.textContent = this.config.adding;
      button.setAttribute('aria-busy','true');
      let accepted = false;
      let uncertain = false;
      try {
        const sections = [];
        document.dispatchEvent(new CustomEvent('cart:push:section.id',{detail:{sections}}));
        const sectionIds = [...new Set(sections)].slice(0,5);
        const response = await fetch(this.root+'cart/add.js', {
          method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json','X-Requested-With':'XMLHttpRequest'},
          body:JSON.stringify({items:[{id:view.id,quantity:1}],sections:sectionIds,sections_url:location.pathname})
        });
        uncertain = response.ok;
        const result = await response.json();
        if (!response.ok) {
          this.status.textContent = result.description || this.config.error;
          cache.clear();
          return;
        }
        accepted = true;
        const cartResponse = await fetch(this.root+'cart.js',{headers:{Accept:'application/json'}});
        if (!cartResponse.ok) throw new Error('cart');
        const cart = await cartResponse.json();
        let rendered = result.sections;
        if (!rendered || sectionIds.some(id=>!rendered[id])) {
          const url = new URL(location.href);
          url.searchParams.set('sections',sectionIds.join(','));
          const htmlResponse = await fetch(url);
          if (!htmlResponse.ok) throw new Error('sections');
          rendered = await htmlResponse.json();
          if (sectionIds.some(id=>!rendered[id])) throw new Error('sections');
        }
        cart.sections = rendered;
        adding = false;
        this.dispatchEvent(new CustomEvent('variant:add',{bubbles:true,detail:{items:result.items||[result],actionAfterATC:'open_cart_drawer',cart}}));
        document.dispatchEvent(new CustomEvent('cart:change',{bubbles:true,detail:{baseEvent:'variant:add',actionAfterATC:'open_cart_drawer',cart}}));
      } catch (error) {
        this.status.textContent = accepted || uncertain ? this.config.refresh : this.config.network;
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } finally {
        adding = false;
        if (this.isConnected) {
          this.querySelectorAll('[data-upsell-add]').forEach(b=>b.disabled=false);
          button.textContent = this.config.add;
          button.removeAttribute('aria-busy');
        }
      }
    }
  }
  customElements.define('ds-cart-upsell', DinoCartUpsell);
}
